import { ComponentType } from 'react';
import { Link } from 'react-router-dom';

import AutoRuleIcon from '~/shared/assets/icons/auto-rule.svg';
import SystemActionIcon from '~/shared/assets/icons/system-action.svg';
import UserIcon from '~/shared/assets/icons/user.svg';
import Tooltip from '~/shared/components/Tooltip';
import Typography from '~/shared/components/Typography';
import { OptLogVolumeActions } from '~/shared/types/api';
import { buildAutoscaleRulePageUrl } from '~/fleet-manager/components/FleetManagerPage/helpers';
import { ActionActorKind, getActionActorDisplay } from '~/shared/utils/actionActor';

interface ActionActorCellProps {
  action: Pick<OptLogVolumeActions, 'type' | 'ruleId' | 'userName'>;
  rowKey: string;
}

const actorIconByKind: Record<ActionActorKind, ComponentType<{ className?: string }>> = {
  system: SystemActionIcon,
  rule: AutoRuleIcon,
  user: UserIcon,
  unknownUser: UserIcon,
};

export default function ActionActorCell({ action, rowKey }: ActionActorCellProps) {
  const actor = getActionActorDisplay(action);
  const tooltipId = actor.tooltip ? `action-actor-${rowKey}` : undefined;
  const Icon = actorIconByKind[actor.kind];
  const ruleLinkLabel =
    actor.kind === 'rule' && actor.ruleId ? `View autoscale rule ${actor.ruleId}` : undefined;

  const label =
    actor.kind === 'rule' && actor.ruleId ? (
      <Link
        to={buildAutoscaleRulePageUrl(actor.ruleId)}
        className="min-w-0 max-w-full"
        title={ruleLinkLabel}
        aria-label={ruleLinkLabel}
        onClick={(event) => event.stopPropagation()}
      >
        <Typography
          color="text-primary-500"
          variant="p3"
          fontWeight={700}
          truncate
          className="underline font-family-inter"
        >
          {actor.label}
        </Typography>
      </Link>
    ) : (
      <Typography variant="p3" fontWeight={500} className="text-[14px] text-gray-600 truncate">
        {actor.label}
      </Typography>
    );

  return (
    <>
      <div className="flex min-w-0 items-center gap-2 overflow-hidden" data-tooltip-id={tooltipId}>
        <Icon className="size-6 shrink-0 text-gray-600" aria-hidden />
        {label}
      </div>
      {tooltipId && <Tooltip id={tooltipId} content={actor.tooltip} place="top" />}
    </>
  );
}
